<template>
  <div class="space-y-6">
    
    <!-- Affichage des conflits de schémas -->
    <div v-if="schemaConflict" class="bg-yellow-50 border border-yellow-200 p-4 rounded-lg">
      <h4 class="font-bold mb-3 text-yellow-800">
        <span v-if="schemaConflict.type === 'uuid_match'">🔄 Mise à jour de schéma détectée</span>
        <span v-else-if="schemaConflict.type === 'similar_schema'">⚠️ Schéma similaire détecté ({{ schemaConflict.similarity_percentage }}% de similarité)</span>
        <span v-else>⚠️ Schéma similaire détecté</span>
      </h4>
      
      <div class="mb-4 p-3 bg-white rounded border">
        <div class="grid grid-cols-2 gap-4 text-sm">
          <div>
            <span class="font-medium text-gray-700">Schéma importé:</span>
            <div class="mt-1">
              <div><strong>Nom:</strong> {{ editableSchema.appellation }}</div>
              <div><strong>Type:</strong> {{ editableSchema.type }}</div>
              <div v-if="parsedSchema?.uuid"><strong>UUID:</strong> 
                <code class="text-xs bg-gray-100 px-1 rounded">{{ parsedSchema.uuid }}</code>
              </div>
            </div>
          </div>
          <div>
            <span class="font-medium text-gray-700">Schéma existant:</span>
            <div class="mt-1">
              <div><strong>Nom:</strong> {{ schemaConflict.existing_schema?.appellation }}</div>
              <div><strong>Type:</strong> {{ schemaConflict.existing_schema?.type }}</div>
              <div><strong>UUID:</strong> 
                <code class="text-xs bg-gray-100 px-1 rounded">{{ schemaConflict.existing_schema?.uuid }}</code>
              </div>
              <div v-if="schemaConflict.existing_schema?.created_at" class="text-xs text-gray-500">
                Créé le {{ formatDate(schemaConflict.existing_schema.created_at) }}
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <!-- Choix d'action pour correspondance nom exacte ou schéma similaire -->
      <div v-if="schemaConflict.type === 'name_match' || schemaConflict.type === 'similar_schema'" class="space-y-3">
        <div class="font-medium text-yellow-800">Action souhaitée :</div>
        <div class="space-y-2">
          <label class="flex items-center p-3 border rounded hover:bg-yellow-50 cursor-pointer">
            <input 
              type="radio" 
              :value="'update'"
              :checked="schemaConflict.action === 'update'"
              class="mr-3"
              @change="updateSchemaConflictAction('update')"
            />
            <div>
              <div class="font-medium">🔄 Mettre à jour le schéma existant</div>
              <div class="text-sm text-gray-600">Les sections et traductions seront fusionnées avec le schéma existant</div>
            </div>
          </label>
          <label class="flex items-center p-3 border rounded hover:bg-yellow-50 cursor-pointer">
            <input 
              type="radio" 
              :value="'create_new'"
              :checked="schemaConflict.action === 'create_new'"
              class="mr-3"
              @change="updateSchemaConflictAction('create_new')"
            />
            <div>
              <div class="font-medium">🆕 Créer un nouveau schéma</div>
              <div class="text-sm text-gray-600">Un schéma indépendant sera créé (le nom sera peut-être ajusté)</div>
            </div>
          </label>
        </div>
        
        <!-- Affichage des schémas similaires multiples si disponibles -->
        <div v-if="schemaConflict.type === 'similar_schema' && schemaConflict.similar_schemas && schemaConflict.similar_schemas.length > 1" class="mt-4 p-3 bg-blue-50 border border-blue-200 rounded">
          <div class="font-medium text-blue-800 mb-2">Autres schémas similaires détectés :</div>
          <div class="space-y-1 text-sm">
            <div v-for="similar in schemaConflict.similar_schemas.slice(1)" :key="similar.schema.uuid" class="flex justify-between">
              <span>{{ similar.schema.appellation }} ({{ similar.schema.type }})</span>
              <span class="text-blue-600">{{ Math.round(similar.similarity * 100) }}%</span>
            </div>
          </div>
        </div>
      </div>
      
      <!-- Message automatique pour correspondance UUID -->
      <div v-else-if="schemaConflict.type === 'uuid_match'" class="p-3 bg-blue-50 border border-blue-200 rounded">
        <div class="font-medium text-blue-800">🔄 Mise à jour automatique</div>
        <div class="text-sm text-blue-700 mt-1">
          L'UUID correspond exactement. Ce schéma sera automatiquement mis à jour avec les nouvelles données.
        </div>
      </div>
    </div>
    
    <!-- Configuration du schéma -->
    <div class="bg-blue-50 p-4 rounded-lg">
      <h4 class="font-bold mb-3 text-blue-900">⚙️ Configuration du schéma</h4>
      
      <!-- Champ appellation -->
      <div class="mb-4">
        <label class="block text-sm font-medium text-gray-700 mb-1">
          Nom du schéma <span class="text-red-500">*</span>
        </label>
        <input 
          v-model="localSchema.appellation"
          type="text"
          class="w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          :class="{ 'border-red-500': validationErrors.some(e => e.includes('nom')) }"
          placeholder="Entrez le nom du schéma"
        />
        <p v-if="validationErrors.some(e => e.includes('nom'))" class="text-red-500 text-xs mt-1">
          Le nom du schéma est obligatoire
        </p>
      </div>
      
      <!-- Sélecteur de type -->
      <div class="mb-4">
        <label class="block text-sm font-medium text-gray-700 mb-1">
          Type de schéma <span class="text-red-500">*</span>
        </label>
        <select 
          v-model="localSchema.type"
          class="w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          :class="{ 'border-red-500': validationErrors.some(e => e.includes('type')) }"
        >
          <option value="">Sélectionnez un type</option>
          <option 
            v-for="schemaType in validSchemaTypes" 
            :key="schemaType.value" 
            :value="schemaType.value"
          >
            {{ schemaType.label }}
          </option>
        </select>
        <p v-if="validationErrors.some(e => e.includes('type'))" class="text-red-500 text-xs mt-1">
          Le type du schéma est obligatoire
        </p>
      </div>
      
      <!-- Champ description -->
      <div class="mb-4">
        <label class="block text-sm font-medium text-gray-700 mb-1">
          Description
        </label>
        <textarea 
          v-model="localSchema.description"
          class="w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          rows="3"
          placeholder="Description du schéma (optionnel)"
        ></textarea>
      </div>
      
      <!-- Résumé des sections -->
      <div class="bg-white p-3 rounded border">
        <div class="grid grid-cols-2 gap-4 text-sm">
          <div>
            <span class="font-medium text-gray-700">Sections à importer:</span>
            <span class="ml-2 font-bold text-blue-600">{{ parsedSchema?.section_list.length || 0 }}</span>
          </div>
          <div v-if="parsedSchema?.version">
            <span class="font-medium text-gray-700">Version du schéma:</span>
            <span class="ml-2 text-gray-600">{{ formatDate(parsedSchema.version) }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Informations sur les traductions détectées -->
    <div v-if="translationsInfo.languages.length > 0" class="bg-green-50 border border-green-200 p-4 rounded-lg">
      <h4 class="font-bold mb-2 text-green-800">🌐 Traductions détectées</h4>
      <div class="text-sm text-green-700">
        <div class="mb-2">
          <strong>Langues disponibles:</strong> {{ translationsInfo.languages.join(', ') }}
        </div>
        <div>
          <strong>Sections avec traductions:</strong> {{ translationsInfo.sectionsWithTranslations }}/{{ parsedSchema?.section_list.length || 0 }}
        </div>
      </div>
    </div>

    <!-- Validation globale -->
    <div v-if="validationErrors.length > 0" class="bg-red-50 border border-red-200 p-4 rounded-lg">
      <h4 class="font-bold mb-2 text-red-800">❌ Erreurs de validation</h4>
      <ul class="text-sm text-red-700 space-y-1">
        <li v-for="error in validationErrors" :key="error" class="flex items-start">
          <span class="text-red-500 mr-1">•</span>
          {{ error }}
        </li>
      </ul>
    </div>

    <!-- Aperçu rapide -->
    <div v-if="isSchemaValid" class="bg-gray-50 p-4 rounded-lg">
      <h4 class="font-bold mb-2 text-gray-900">👁️ Aperçu du schéma configuré</h4>
      <div class="grid grid-cols-2 gap-4 text-sm">
        <div>
          <span class="font-medium text-gray-700">Nom final:</span>
          <span class="ml-2">{{ editableSchema.appellation }}</span>
        </div>
        <div>
          <span class="font-medium text-gray-700">Type:</span>
          <span class="ml-2">{{ editableSchema.type }}</span>
        </div>
      </div>
      <div v-if="editableSchema.description" class="mt-2">
        <span class="font-medium text-gray-700">Description:</span>
        <p class="text-gray-600 mt-1">{{ editableSchema.description }}</p>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import type { SchemaJson } from '@/api/types/core/schemaJson';
import type { SchemaConflict } from '../../../composables/useSchemaImport';
import { SchemaImportUtils } from '../../../utils/schemaImportUtils';

// Props
const props = defineProps<{
  parsedSchema: SchemaJson | null;
  schemaConflict: SchemaConflict | null;
  editableSchema: {
    appellation: string;
    type: string;
    description: string;
  };
  isSchemaValid: boolean;
  validationErrors: string[];
  validSchemaTypes: Array<{ value: string; label: string }>;
}>();

// Emits
const emit = defineEmits<{
  'update-schema-conflict-action': [action: 'update' | 'create_new'];
  'update:editableSchema': [schema: { appellation: string; type: string; description: string }];
}>();

// Variables locales pour éviter la mutation directe des props
const localSchema = ref({
  appellation: props.editableSchema.appellation,
  type: props.editableSchema.type,
  description: props.editableSchema.description
});

// Watchers pour synchroniser avec les props
watch(() => props.editableSchema, (newValue) => {
  localSchema.value = {
    appellation: newValue.appellation,
    type: newValue.type,
    description: newValue.description
  };
}, { deep: true });

// Watcher pour émettre les changements
watch(localSchema, (newValue) => {
  emit('update:editableSchema', newValue);
}, { deep: true });

// Informations sur les traductions
const translationsInfo = computed(() => {
  if (!props.parsedSchema) {
    return { languages: [], sectionsWithTranslations: 0 };
  }

  const languages = new Set<string>();
  let sectionsWithTranslations = 0;

  props.parsedSchema.section_list.forEach(section => {
    if (section.translations && Object.keys(section.translations).length > 0) {
      sectionsWithTranslations++;
      Object.keys(section.translations).forEach(lang => languages.add(lang.toUpperCase()));
    }
  });

  return {
    languages: Array.from(languages).sort(),
    sectionsWithTranslations
  };
});

// Méthodes
const updateSchemaConflictAction = (action: 'update' | 'create_new'): void => {
  emit('update-schema-conflict-action', action);
};

const formatDate = (isoString: string): string => {
  return SchemaImportUtils.formatDate(isoString);
};
</script> 